import React, { useCallback, useMemo, useState } from "react";
import { useGlobalModalStack } from "@wtc/ui";
import type { SavedViewConfig, SerializedViewState } from "../../UniversalCRMTable/types";
import type { SavedView, SavedViewVisibility } from "@/lib/types/saved-views";
import {
  useSavedViews,
  useSavedViewFavorites,
  useCreateSavedView,
  useUpdateSavedView,
  useDeleteSavedView,
  useToggleSavedViewFavorite,
  savedViewToConfig,
} from "@/lib/hooks/api/saved-views";

interface UseSavedViewsStateProps {
  resource: string; // entityName
  configSavedViews: SavedViewConfig[]; // from config.savedViews
  serializeFilters: (() => SerializedViewState) | null;
  applyFilters: ((filters: SerializedViewState, isManualSelection?: boolean) => void) | null;
  activeViewId: string | null;
  setActiveViewId: (id: string | null) => void;
}

/**
 * Hook that manages saved views state for UniversalEntityPage
 * Handles API calls, mutations, modal state, and form state
 */
export function useSavedViewsState({
  resource,
  configSavedViews,
  serializeFilters,
  applyFilters,
  activeViewId,
  setActiveViewId,
}: UseSavedViewsStateProps) {
  const { openModal } = useGlobalModalStack();

  // ============================================================================
  // API HOOKS - Call unconditionally
  // ============================================================================
  const { data: savedViews = [] } = useSavedViews(resource);
  const { data: favorites = [] } = useSavedViewFavorites(resource);
  const createView = useCreateSavedView();
  const updateView = useUpdateSavedView();
  const deleteView = useDeleteSavedView();
  const toggleFavorite = useToggleSavedViewFavorite(resource);

  // ============================================================================
  // MODAL STATE - keeping for backward compatibility, but modal is now controlled by global stack
  // ============================================================================
  const [saveAsOpen, setSaveAsOpen] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deletingViewId, setDeletingViewId] = useState<string | null>(null);

  // ============================================================================
  // FORM STATE
  // ============================================================================
  const [saveAsName, setSaveAsName] = useState("");
  const [shareVisibility, setShareVisibility] = useState<SavedViewVisibility>("private");
  const [shareUserIds, setShareUserIds] = useState<string[]>([]);

  // ============================================================================
  // COMPUTED VALUES
  // ============================================================================
  // Combine config views and saved views
  const allViews = useMemo(() => {
    const configViews = configSavedViews || [];
    const savedViewConfigs = savedViews.map(savedViewToConfig);
    return [...configViews, ...savedViewConfigs];
  }, [configSavedViews, savedViews]);

  // Get config views for reference
  const configViews = useMemo(() => configSavedViews || [], [configSavedViews]);

  // Get selected view
  const selectedView = useMemo(() => {
    if (!activeViewId) return null;
    return allViews.find((v) => v.id === activeViewId);
  }, [activeViewId, allViews]);

  const isFavorite = useMemo(() => {
    return activeViewId ? favorites.includes(activeViewId) : false;
  }, [activeViewId, favorites]);

  // ============================================================================
  // HANDLERS
  // ============================================================================
  const handleSaveAs = useCallback(() => {
    setSaveAsName(selectedView ? `${selectedView.name}` : "");
    openModal("tabs-save-view-as");
  }, [selectedView, openModal]);

  const handleShare = useCallback(
    (viewId: string) => {
      const view = allViews.find((v) => v.id === viewId);
      if (!view) {
        return;
      }

      // Only allow sharing user-created views, not config presets
      if (!("created_by" in view)) return;

      // Set the active view ID so selectedView is available in context
      setActiveViewId(viewId);

      setShareVisibility((view as SavedView).visibility);
      setShareUserIds((view as SavedView).shared_with_user_ids || []);
      openModal("tabs-share-view");
    },
    [allViews, setActiveViewId, openModal, activeViewId]
  );

  const handleDelete = useCallback(
    (viewId: string) => {
      const view = allViews.find((v) => v.id === viewId);
      if (!view) return;

      // Only allow deleting user-created views, not config presets
      if (!("created_by" in view)) return;

      // Set the active view ID so selectedView is available in context
      setActiveViewId(viewId);
      
      setDeletingViewId(viewId);
      openModal("tabs-delete-view");
    },
    [allViews, setActiveViewId, openModal]
  );

  // ============================================================================
  // RETURN STATE OBJECT
  // ============================================================================
  return {
    savedViews,
    favorites,
    allViews,
    configViews,
    selectedViewId: activeViewId || undefined,
    createView,
    updateView,
    deleteView,
    toggleFavorite,
    handleSaveAs,
    handleShare,
    handleDelete,
    saveAsOpen,
    setSaveAsOpen,
    shareOpen,
    setShareOpen,
    deleteOpen,
    setDeleteOpen,
    deletingViewId,
    setDeletingViewId,
    saveAsName,
    setSaveAsName,
    shareVisibility,
    setShareVisibility,
    shareUserIds,
    setShareUserIds,
    serializeFilters,
  };
}
